import { getMediaBaseUrl } from "../../config/env";
import { formatAuditUserLabel } from "../../lib/listingSync.utils";
import type {
	CabanaAddOn,
	CabanaBookingDetail,
	CabanaFaqItem,
	CabanaListItem,
	CabanaMediaItem,
	CabanaSeatingType,
	CabanaSevenRoomsIntegration,
} from "../../pages/CabanaBooking/cabanaBooking.types";
import {
	CabanaSourceType,
	type CabanaUpsertPayload,
	CabanaVenueStatus,
} from "./cabana.types";

const CABANA_MEDIA_BASE_URL = getMediaBaseUrl();

export function resolveCabanaMediaUrl(path: string): string {
	if (!path) return "";
	if (path.startsWith("http://") || path.startsWith("https://")) return path;
	if (path.startsWith("blob:")) return path;
	return `${CABANA_MEDIA_BASE_URL}/${path.replace(/^\/+/, "")}`;
}

function displayId(value: unknown): string {
	if (value == null || value === "") return "—";
	return String(value);
}

function mapSource(raw: unknown): CabanaBookingDetail["source"] {
	const v = String(raw ?? "").toUpperCase();
	return v === CabanaSourceType.SEVENROOMS
		? CabanaSourceType.SEVENROOMS
		: CabanaSourceType.MANUAL;
}

function mapPublicationStatus(raw: unknown): CabanaBookingDetail["status"] {
	const v = String(raw ?? "").toUpperCase();
	if (v === CabanaVenueStatus.PUBLISHED || v === "ACTIVE") {
		return CabanaVenueStatus.PUBLISHED;
	}
	if (v === CabanaVenueStatus.INACTIVE) return CabanaVenueStatus.INACTIVE;
	return CabanaVenueStatus.DRAFT;
}

function mapMediaItems(
	bannerImage: unknown,
	mediaRaw: unknown,
): CabanaMediaItem[] {
	const bannerPath =
		typeof bannerImage === "string" && bannerImage.trim()
			? bannerImage.trim()
			: "";
	const paths = Array.isArray(mediaRaw)
		? mediaRaw
				.filter(
					(p): p is string => typeof p === "string" && p.trim().length > 0,
				)
				.map((p) => p.trim())
		: [];

	const uniquePaths = [
		...(bannerPath ? [bannerPath] : []),
		...paths.filter((p) => p !== bannerPath),
	];

	return uniquePaths.map((path, index) => {
		const fileName = path.split("/").pop() ?? "media";
		return {
			id: `cmedia-${index}-${fileName}`,
			url: resolveCabanaMediaUrl(path),
			storageKey: path,
			fileName,
			sizeLabel: "",
			kind: path === bannerPath ? "hero" : "image",
		};
	});
}

function mapFaqs(raw: unknown): CabanaFaqItem[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = (item ?? {}) as Record<string, unknown>;
		return {
			id: String(row.id ?? `cf-${index}`),
			questionHtml: String(row.question ?? ""),
			answerHtml: String(row.answer ?? ""),
		};
	});
}

function mapSeatingTypes(raw: unknown): CabanaSeatingType[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = (item ?? {}) as Record<string, unknown>;
		const imagePath = String(row.imageUrl ?? row.image ?? row.imagePath ?? "");
		return {
			id: String(row.id ?? `st-${index}`),
			name: String(row.name ?? ""),
			description: String(row.description ?? ""),
			imageUrl: imagePath ? resolveCabanaMediaUrl(imagePath) : "",
		};
	});
}

function mapAddOns(raw: unknown): CabanaAddOn[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = (item ?? {}) as Record<string, unknown>;
		const imagePath = String(row.imageUrl ?? row.image ?? row.imagePath ?? "");
		return {
			id: String(row.id ?? `ad-${index}`),
			name: String(row.name ?? ""),
			description: String(row.description ?? ""),
			priceWithCurrency: String(
				row.priceWithCurrency ?? row.price ?? row.amount ?? "",
			),
			imageUrl: imagePath ? resolveCabanaMediaUrl(imagePath) : "",
		};
	});
}

function mapSevenRooms(
	row: Record<string, unknown>,
): CabanaSevenRoomsIntegration | null {
	if (mapSource(row.source) !== CabanaSourceType.SEVENROOMS) return null;
	const sevenRoomsId = row.sevenRoomsId;
	const venueId = row.venueId;
	if (!sevenRoomsId && !venueId) return null;

	const prefs = Array.isArray(row.sevenRoomsPreferences)
		? row.sevenRoomsPreferences
		: [];
	const specialOccasionTags: string[] = [];
	const allergyTags: string[] = [];
	for (const pref of prefs) {
		const p = (pref ?? {}) as Record<string, unknown>;
		const category = String(p.category ?? p.type ?? "").toLowerCase();
		const label = String(p.label ?? p.name ?? p.value ?? "").trim();
		if (!label) continue;
		if (category.includes("occasion") || category.includes("special")) {
			specialOccasionTags.push(label);
		} else if (category.includes("allerg") || category.includes("diet")) {
			allergyTags.push(label);
		}
	}

	return {
		sevenRoomsId: displayId(sevenRoomsId),
		venueId: displayId(venueId),
		reservationWindowDays: Number(row.reservationWindowDays ?? 0) || 0,
		guestCountLimit: Number(row.guestCountLimit ?? 0) || 0,
		specialOccasionTags,
		allergyTags,
		termsAndConditions: String(row.termsAndConditions ?? ""),
	};
}

export function mapCabanaListItem(raw: unknown): CabanaListItem {
	const row = (raw ?? {}) as Record<string, unknown>;
	const propertyName =
		typeof row.propertyName === "string"
			? row.propertyName
			: typeof row.property === "object" &&
					row.property &&
					"name" in row.property
				? String((row.property as { name?: string }).name ?? "")
				: "";

	return {
		id: String(row.id ?? ""),
		name: String(row.name ?? ""),
		propertyId: String(row.propertyId ?? ""),
		propertyName,
		restaurantName: String(row.restaurantName ?? ""),
		source: mapSource(row.source),
		sevenRoomsId: displayId(row.sevenRoomsId),
		venueId: displayId(row.venueId),
		status: mapPublicationStatus(row.publicationStatus ?? row.status),
		lastUpdatedBy: formatAuditUserLabel(row.lastUpdatedBy ?? row.updatedBy),
		lastUpdatedAt: String(row.lastUpdatedAt ?? row.updatedAt ?? ""),
	};
}

export function mapCabanaDetail(raw: unknown): CabanaBookingDetail {
	const row = (raw ?? {}) as Record<string, unknown>;
	const property =
		row.property && typeof row.property === "object"
			? (row.property as Record<string, unknown>)
			: {};
	const propertyId = String(row.propertyId ?? property.id ?? "");
	const propertyName = String(row.propertyName ?? property.name ?? "");
	const media = mapMediaItems(row.bannerImage, row.media);

	return {
		id: String(row.id ?? ""),
		propertyId,
		name: String(row.name ?? ""),
		propertyName,
		restaurantName: String(row.restaurantName ?? ""),
		source: mapSource(row.source),
		sevenRoomsId: displayId(row.sevenRoomsId),
		venueId: displayId(row.venueId),
		status: mapPublicationStatus(row.publicationStatus ?? row.status),
		lastUpdatedBy: formatAuditUserLabel(
			row.lastUpdatedBy ?? row.updatedBy ?? row.createdBy,
		),
		lastUpdatedAt: String(
			row.lastUpdatedAt ?? row.updatedAt ?? row.createdAt ?? "",
		),
		phone: String(row.phoneNo ?? row.phone ?? ""),
		address: String(row.address ?? ""),
		mapUrl: String(row.googleLocation ?? row.mapUrl ?? ""),
		description: String(row.description ?? ""),
		media,
		faqs: mapFaqs(row.faqs),
		sevenRooms: mapSevenRooms(row),
		seatingTypes: mapSeatingTypes(row.seatingTypes),
		addOns: mapAddOns(row.addOns),
	};
}

export function mediaItemToStorageKey(item: CabanaMediaItem): string {
	if (item.storageKey?.trim()) return item.storageKey.trim();
	const url = item.url.trim();
	if (!url) return "";
	if (!url.startsWith("http")) return url.replace(/^\/+/, "");
	try {
		const parsed = new URL(url);
		return parsed.pathname.replace(/^\/+/, "");
	} catch {
		return url;
	}
}

export function cabanaFormToApiPayload(
	form: import("../../pages/CabanaBooking/cabanaBooking.form").CabanaFormState,
	propertyId: string,
	status: CabanaVenueStatus,
): CabanaUpsertPayload {
	const hero = form.media.find((m) => m.kind === "hero");
	const gallery = form.media.filter((m) => m.kind !== "hero");
	const bannerImage = hero ? mediaItemToStorageKey(hero) : undefined;
	const mediaKeys = gallery
		.map(mediaItemToStorageKey)
		.filter((key) => key.length > 0);

	return {
		name: form.name.trim(),
		propertyId,
		phoneNo: form.phone.trim() || undefined,
		status,
		address: form.address.trim() || undefined,
		googleLocation: form.mapUrl.trim() || undefined,
		description: form.description.trim() || undefined,
		restaurantName: form.restaurantName.trim() || undefined,
		bannerImage,
		media: mediaKeys.length ? mediaKeys : undefined,
		faqs: form.faqs
			.filter((f) => f.questionHtml.trim() || f.answerHtml.trim())
			.map((f) => ({
				question: f.questionHtml.trim(),
				answer: f.answerHtml.trim(),
			})),
	};
}
